import { useEffect, useRef, useState } from "react";
import { EYEBROW_CLASS } from "../lib/ui.js";
import { useCurrentUser } from "../lib/userInfo.js";
import { useMyPermissions, roleNames } from "../lib/permissions.js";
import { AUTH_REQUIRED } from "../lib/featureFlags.js";
import HoverTooltip from "./HoverTooltip.jsx";
import LogoutDialog from "./LogoutDialog.jsx";

// Key/value line for the identity tooltip. Value wraps (break-all) rather
// than forcing the 300px card wider.
function IdentityField({ label, value }) {
  return (
    <div className="flex gap-[8px]">
      <span className="w-[42px] shrink-0 text-[color:var(--muted)]">{label}</span>
      <span className="min-w-0 flex-1 break-all text-[color:var(--ink)]">
        {value || "—"}
      </span>
    </div>
  );
}

// Contents of the tooltip over the tenant label: who's signed in and the RBAC
// role(s) the session holds. A read-only awareness affordance — handy when a
// redacted surface (hidden trace log, gated action) prompts "which role am I
// in right now?". Role names come from the same posture (permissions.js) the
// gates read, falling back to the /v1/me membership role while that posture is
// still loading.
function IdentityTooltip({ user, caps }) {
  const roles = roleNames(caps);
  const roleLabel = roles.length
    ? roles.join(", ")
    : user?.currentTenant?.role || null;

  return (
    <div className="grid gap-[3px] text-[11px] leading-[1.35]">
      <div className={EYEBROW_CLASS}>Signed in</div>
      {user?.email ? (
        <div className="break-all text-[12px] font-medium text-[color:var(--ink)]">
          {user.email}
        </div>
      ) : null}
      {roleLabel ? <IdentityField label="Role" value={roleLabel} /> : null}
    </div>
  );
}

// A head and shoulders, drawn as the theme toggle's sun is.
function ProfileIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="5.6" r="2.6" />
      <path d="M3 13.6c.7-2.4 2.7-3.8 5-3.8s4.3 1.4 5 3.8" />
    </svg>
  );
}

function ThemeToggleIcon({ theme }) {
  if (theme === "dark") {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="8" cy="8" r="2.6" />
        <path d="M8 1.8v1.7" />
        <path d="M8 12.5v1.7" />
        <path d="M1.8 8h1.7" />
        <path d="M12.5 8h1.7" />
        <path d="M3.6 3.6l1.2 1.2" />
        <path d="M11.2 11.2l1.2 1.2" />
        <path d="M12.4 3.6l-1.2 1.2" />
        <path d="M4.8 11.2l-1.2 1.2" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.9 1.9a5.9 5.9 0 1 0 3.2 10.9A6.4 6.4 0 0 1 10.9 1.9Z" />
    </svg>
  );
}

export default function SidebarFooter({
  theme,
  onToggleTheme,
  onOpenAccount,
  onSendFeedback,
}) {
  const user = useCurrentUser();
  const caps = useMyPermissions();
  const orgName = user?.currentTenant?.tenantName || "—";
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    if (!userMenuOpen) return undefined;
    const close = (event) => {
      if (event.type === "keydown" && event.key !== "Escape") return;
      if (event.type === "pointerdown" && userMenuRef.current?.contains(event.target)) return;
      setUserMenuOpen(false);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
    };
  }, [userMenuOpen]);

  return (
    <div
      data-sidebar-foot
      // pb-[16px] is calibrated to the bottom of the OriginMark on the
      // rail — do NOT decrease it. Two-column layout (org name +
      // controls); the previous hamburger toggle was removed once
      // the rail's mode buttons took over mode-switching.
      // A band as tall as the workspace's foot bands (the board's controls,
      // the chat's composer) over the main column's 10px gutter and a frame's
      // edge, so the drawer's foot rules off at the same line as theirs.
      className="grid h-[43px] max-h-[56px] grid-cols-[minmax(0,1fr)_auto] items-center gap-[6px] self-end overflow-visible border-t border-[color:var(--line)] bg-[color:var(--bg)] pb-[11px] text-[11px] text-[color:var(--muted)]"
    >
      <div ref={userMenuRef} className="relative min-w-0">
        <button
          type="button"
          className="flex w-full min-w-0 items-center gap-[5px] border-0 bg-transparent p-0 text-left text-[12px] leading-[22px] font-medium text-[color:var(--ink)] cursor-pointer"
          aria-label={`Open signed-in user menu${user?.email ? ` for ${user.email}` : ""}`}
          aria-haspopup="menu"
          aria-expanded={userMenuOpen}
          onClick={() => setUserMenuOpen((open) => !open)}
        >
          <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{orgName}</span>
          <span aria-hidden="true" className="text-[9px] text-[color:var(--muted-soft)]">{userMenuOpen ? "▾" : "▴"}</span>
        </button>
        {userMenuOpen ? (
          <div
            role="menu"
            className="absolute bottom-[30px] left-0 z-50 grid w-[280px] gap-[4px] border border-[color:var(--line-strong)] bg-[color:var(--bg)] p-[8px] shadow-lg"
          >
            <div className="border-b border-[color:var(--line)] px-[4px] pb-[7px]">
              <IdentityTooltip user={user} caps={caps} />
            </div>
            <button
              type="button"
              role="menuitem"
              className="px-[6px] py-[6px] text-left text-[11px] text-[color:var(--muted)] hover:bg-[color:var(--gray-fill)] hover:text-[color:var(--ink)]"
              onClick={() => { setUserMenuOpen(false); onSendFeedback?.(); }}
            >
              Send feedback
            </button>
          </div>
        ) : null}
      </div>
      {/* Cells against the band, as the board's controls and the composer's
          buttons are along the same edge. */}
      <div className="flex items-stretch self-stretch">
        {/* An icon cell, as the theme's is, so the org's name keeps its room. */}
        <HoverTooltip placement="top" label="Profile">
          <button
            className="grid w-[30px] place-items-center border-l border-[color:var(--line-soft)] p-0 text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] [&_svg]:block [&_svg]:h-3 [&_svg]:w-3"
            type="button"
            onClick={() => { setUserMenuOpen(false); onOpenAccount?.(); }}
            aria-label="Profile"
          >
            <ProfileIcon />
          </button>
        </HoverTooltip>
        {onToggleTheme ? (
          <button
            className="grid w-[30px] place-items-center border-l border-[color:var(--line-soft)] p-0 text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] [&_svg]:block [&_svg]:h-3 [&_svg]:w-3"
            type="button"
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            onClick={onToggleTheme}
          >
            <ThemeToggleIcon theme={theme} />
          </button>
        ) : null}
        {AUTH_REQUIRED ? (
          <button
            className="flex cursor-pointer items-center border-l border-[color:var(--line-soft)] px-[9px] text-[11px] leading-[16px] text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)]"
            type="button"
            onClick={() => setLogoutOpen(true)}
            aria-label="Log out"
          >
            Log out
          </button>
        ) : null}
      </div>
      {AUTH_REQUIRED ? (
        <LogoutDialog open={logoutOpen} onClose={() => setLogoutOpen(false)} />
      ) : null}
    </div>
  );
}
